import { useState } from 'react';
import { format } from 'date-fns';
import { Filter } from 'lucide-react';
import { useIntl } from 'react-intl';

import Button from 'src/componentes/button';
import DatePicker from 'src/componentes/date-picker';
import Drawer from 'src/componentes/drawer';
import Select2 from 'src/componentes/select2';
import useAuth from 'src/hooks/useAuth';
import formatDateFns from 'src/utils/formatDateFns';
import { capitalizeFirstLetter } from 'src/utils/string';
import { getDataAplicacaoFormularioGlobal } from 'src/paginas/avaliacao-desempenho/PesquisaClima/utils';

const criarRascunho = (filtro) => ({
  dataInicio: filtro.dataInicio,
  dataTermino: filtro.dataTermino,
  idsSelecionados: filtro.idsSelecionados,
});
const formatarDia = (value) => (value ? format(value, 'yyyy-MM-dd') : null);

function EvolucaoFiltroAplicacoes({ filtro }) {
  const intl = useIntl();
  const { user } = useAuth();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);
  const [open, setOpen] = useState(false);
  const [rascunho, setRascunho] = useState(() => criarRascunho(filtro));

  const formatarData = (value) => {
    const data = getDataAplicacaoFormularioGlobal(value);
    return data && !Number.isNaN(data.getTime())
      ? format(data, currentDisplay, { locale: currentLocale })
      : '-';
  };
  const opcoes = filtro.aplicacoes.map((aplicacao) => ({
    id: String(aplicacao.formularioUsoId),
    descricao: intl.formatMessage(
      { id: 'labelPeriodoAplicacaoPesquisaClima' },
      {
        dataInicio: formatarData(aplicacao.dataInicio),
        dataTermino: formatarData(aplicacao.dataTermino),
      }
    ),
  }));
  const mesmoPeriodo =
    formatarDia(rascunho.dataInicio) === formatarDia(filtro.dataInicio) &&
    formatarDia(rascunho.dataTermino) === formatarDia(filtro.dataTermino);
  const idsMarcados =
    rascunho.idsSelecionados ??
    (filtro.idsSelecionados === null && mesmoPeriodo ? filtro.idsResposta : []);
  const selecionadas = opcoes.filter((opcao) => idsMarcados.includes(opcao.id));
  const periodoAtivo = !!rascunho.dataInicio || !!rascunho.dataTermino;
  const periodoInvalido =
    !!rascunho.dataInicio &&
    !!rascunho.dataTermino &&
    formatarDia(rascunho.dataInicio) > formatarDia(rascunho.dataTermino);
  const semAplicacoesSelecionadas =
    rascunho.idsSelecionados !== null && rascunho.idsSelecionados.length === 0;
  const resumoAplicacoes =
    !rascunho.idsSelecionados && !periodoInvalido
      ? periodoAtivo
        ? intl.formatMessage({ id: 'labelEvolucaoUsosDoPeriodo' })
        : filtro.idsSelecionados === null &&
          mesmoPeriodo &&
          filtro.idsResposta.length > 0
        ? intl.formatMessage(
            { id: 'msgEvolucaoUltimasAplicacoes' },
            { quantidade: filtro.idsResposta.length }
          )
        : null
      : null;

  const onOpen = () => {
    setRascunho(criarRascunho(filtro));
    setOpen(true);
  };
  const onAplicar = () => {
    if (periodoInvalido || semAplicacoesSelecionadas) return;
    filtro.onAplicar(rascunho);
    setOpen(false);
  };
  const onLimpar = () => {
    filtro.onLimpar();
    setOpen(false);
  };

  return (
    <>
      <Button
        aria-label={intl.formatMessage({ id: 'label.filtrar' })}
        color={filtro.temFiltros ? 'primary' : 'outline-primary'}
        onClick={onOpen}
        size="sm"
        style={{ alignSelf: 'center', height: 32, padding: '4px 8px' }}
        title={intl.formatMessage({ id: 'label.filtrar' })}
        type="button"
      >
        <Filter size={16} />
      </Button>
      <Drawer
        open={open}
        close={() => setOpen(false)}
        title={
          <div
            className="font-weight-bold pt-4 pb-4"
            style={{ color: '#404040', fontSize: '1.25rem' }}
          >
            {capitalizeFirstLetter(intl.formatMessage({ id: 'filtros' }))}
          </div>
        }
        position="right"
        size={430}
        zIndex={12000}
        focusTrap
        focusTrapPriority={3}
        actions={
          <div
            style={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <button
              onClick={onLimpar}
              style={{
                background: 'transparent',
                border: 0,
                color: '#0D7E94',
                cursor: 'pointer',
                fontSize: 16,
                fontWeight: 'bold',
                padding: 0,
              }}
              type="button"
            >
              {intl.formatMessage({ id: 'limparTodos' })}
            </button>
            <Button
              color="primary"
              disabled={periodoInvalido || semAplicacoesSelecionadas}
              leftIcon={Filter}
              onClick={onAplicar}
              style={{ width: '7rem' }}
              type="button"
            >
              {intl.formatMessage({ id: 'label.filtrar' })}
            </Button>
          </div>
        }
        actionsStyle={{ borderTop: '1px solid #e4eaec', padding: 20 }}
        bodyStyle={{ overflowX: 'hidden', overflowY: 'auto' }}
        headerStyle={{
          borderBottom: '1px solid #e4eaec',
          paddingBottom: 0,
          paddingLeft: 20,
          paddingRight: 20,
          paddingTop: 0,
        }}
      >
        <DatePicker
          autoFormatDate
          model={{
            label: intl.formatMessage({ id: 'labelEvolucaoPeriodoInicio' }),
            value: rascunho.dataInicio,
            requestChange: (value) =>
              setRascunho((current) => ({ ...current, dataInicio: value })),
          }}
        />
        <DatePicker
          autoFormatDate
          model={{
            label: intl.formatMessage({ id: 'labelEvolucaoPeriodoFim' }),
            value: rascunho.dataTermino,
            requestChange: (value) =>
              setRascunho((current) => ({ ...current, dataTermino: value })),
          }}
        />
        <Select2
          clearable
          compactMulti
          labelKey="descricao"
          multi
          options={opcoes}
          valueKey="id"
          model={{
            label: intl.formatMessage({ id: 'labelEvolucaoUsosEspecificos' }),
            value: selecionadas,
            requestChange: (valores) =>
              setRascunho((current) => ({
                ...current,
                idsSelecionados: valores?.length
                  ? valores.map((opcao) => String(opcao.id))
                  : current.idsSelecionados === null
                  ? []
                  : null,
              })),
          }}
        />
        {periodoInvalido && (
          <div role="alert" style={{ color: '#D92D20', fontSize: 12 }}>
            {intl.formatMessage({ id: 'msgEvolucaoPeriodoInvalido' })}
          </div>
        )}
        {semAplicacoesSelecionadas && (
          <div style={{ color: '#667085', fontSize: 12 }}>
            {intl.formatMessage({ id: 'msgEvolucaoSelecioneAplicacoes' })}
          </div>
        )}
        {resumoAplicacoes && (
          <div style={{ color: '#667085', fontSize: 12 }}>
            {resumoAplicacoes}
          </div>
        )}
      </Drawer>
    </>
  );
}

export default EvolucaoFiltroAplicacoes;
